import { AUDIT_VIEW_ACTION, normalizeAuditLogFilters } from './filterRules';
import { parseAuditId, parseStatusCode } from './filterValidation';
import type { AuditLogFilterKey, AuditLogFilters } from './types';
import { AUDIT_ACTOR_TYPES, AUDIT_IMPERSONATION, AUDIT_METHODS, AUDIT_OUTCOMES } from './types';

/**
 * "1" shows audit log views. Views are hidden by default, and a URL can only record a
 * departure from the default, so the absence of this key is what hides them.
 */
const AUDIT_SHOW_VIEWS_PARAM = 'showViews';

export type AuditLogUrlKey = AuditLogFilterKey | typeof AUDIT_SHOW_VIEWS_PARAM;

/** Every key the page owns. useQueryParams merges, so a removed filter must be null. */
export type AuditLogUrlParams = Record<AuditLogUrlKey, string | string[] | null>;

const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})$/;

function firstString(value: unknown): string | undefined {
    if (typeof value === 'string') return value;
    if (!Array.isArray(value)) return undefined;
    return value.find((item): item is string => typeof item === 'string');
}

/** Repeated keys and comma lists both work, as they do for the backend. */
function strings(value: unknown): string[] {
    const items: unknown[] = Array.isArray(value) ? value : [value];
    return items
        .filter((item): item is string => typeof item === 'string')
        .flatMap((item) => item.split(','))
        .map((item) => item.trim());
}

function oneOf<T extends string>(allowed: readonly T[], value: unknown): T | undefined {
    const text = firstString(value)?.trim();
    return allowed.find((known) => known === text);
}

function listOf<T extends string>(allowed: readonly T[], value: unknown): T[] {
    const texts = strings(value);
    return allowed.filter((known) => texts.includes(known));
}

function instant(value: unknown): Date | undefined {
    const text = firstString(value)?.trim();
    if (text === undefined || !INSTANT.test(text)) return undefined;
    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? undefined : date;
}

function id(value: unknown): number | undefined {
    const text = firstString(value);
    return text === undefined ? undefined : parseAuditId(text);
}

/**
 * The URL is typed by hand as often as it is followed, so every value is narrowed from
 * `unknown` and anything malformed is dropped. Only filter values are read: names and
 * labels always come from loaded data, so an edited link cannot put a false name on
 * an audit screen.
 */
export function parseAuditLogFilters(query: Readonly<Record<string, unknown>>): AuditLogFilters {
    const showViews = firstString(query[AUDIT_SHOW_VIEWS_PARAM])?.trim() === '1';
    const excluded = strings(query.excludeActions).filter((action) => action !== AUDIT_VIEW_ACTION);
    const statusText = firstString(query.statusCode);
    return normalizeAuditLogFilters({
        from: instant(query.from),
        to: instant(query.to),
        entryId: firstString(query.entryId),
        actorEmail: firstString(query.actorEmail),
        actorType: listOf(AUDIT_ACTOR_TYPES, query.actorType),
        actorUserId: id(query.actorUserId),
        impersonation: oneOf(AUDIT_IMPERSONATION, query.impersonation),
        impersonatorUserId: id(query.impersonatorUserId),
        impersonatorEmail: firstString(query.impersonatorEmail),
        actions: strings(query.actions),
        action: firstString(query.action),
        excludeActions: showViews ? excluded : [AUDIT_VIEW_ACTION, ...excluded],
        entityType: strings(query.entityType),
        entityId: firstString(query.entityId),
        entityLabel: firstString(query.entityLabel),
        clientId: id(query.clientId),
        locationId: id(query.locationId),
        cageId: id(query.cageId),
        outcome: listOf(AUDIT_OUTCOMES, query.outcome),
        statusCode: statusText === undefined ? undefined : parseStatusCode(statusText),
        requestId: firstString(query.requestId),
        ip: firstString(query.ip),
        userAgent: firstString(query.userAgent),
        method: listOf(AUDIT_METHODS, query.method),
        route: firstString(query.route),
        changedField: firstString(query.changedField),
        changedFrom: firstString(query.changedFrom),
        changedTo: firstString(query.changedTo),
        metadataPath: firstString(query.metadataPath),
        metadataValue: firstString(query.metadataValue),
    });
}

function text(value: string | number | undefined): string | null {
    return value === undefined ? null : String(value);
}

function list(values: readonly string[] | undefined): string[] | null {
    return values === undefined || values.length === 0 ? null : [...values];
}

function date(value: Date | undefined): string | null {
    return value === undefined ? null : value.toISOString();
}

export function serializeAuditLogFilters(filters: AuditLogFilters): AuditLogUrlParams {
    const f = normalizeAuditLogFilters(filters);
    const viewsHidden = f.excludeActions?.includes(AUDIT_VIEW_ACTION) ?? false;
    return {
        from: date(f.from),
        to: date(f.to),
        entryId: text(f.entryId),
        actorEmail: text(f.actorEmail),
        actorType: list(f.actorType),
        actorUserId: text(f.actorUserId),
        impersonation: text(f.impersonation),
        impersonatorUserId: text(f.impersonatorUserId),
        impersonatorEmail: text(f.impersonatorEmail),
        actions: list(f.actions),
        action: text(f.action),
        excludeActions: list(f.excludeActions?.filter((action) => action !== AUDIT_VIEW_ACTION)),
        [AUDIT_SHOW_VIEWS_PARAM]: viewsHidden ? null : '1',
        entityType: list(f.entityType),
        entityId: text(f.entityId),
        entityLabel: text(f.entityLabel),
        clientId: text(f.clientId),
        locationId: text(f.locationId),
        cageId: text(f.cageId),
        outcome: list(f.outcome),
        statusCode: text(f.statusCode),
        requestId: text(f.requestId),
        ip: text(f.ip),
        userAgent: text(f.userAgent),
        method: list(f.method),
        route: text(f.route),
        changedField: text(f.changedField),
        changedFrom: text(f.changedFrom),
        changedTo: text(f.changedTo),
        metadataPath: text(f.metadataPath),
        metadataValue: text(f.metadataValue),
    };
}
